import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { companyApi } from '../lib/api.ts';
import { CenteredSpinner } from '../components/Spinner.tsx';
import { CompanyStatusBadge } from '../components/StatusBadge.tsx';
import { uploadDocStats } from '../lib/docStats.ts';
import { expiryState, formatDateOnly, type ExpiryState } from '../lib/expiry.ts';
import type { Company, CompanyStatus, Submission, SubmissionFile, Questionnaire, Question } from '../lib/types.ts';
import { computeProgress } from '../lib/visibility.ts';
import {
  questionnaireActionItems,
  isQuestionnaireSubmitted,
  actionReasonSummary,
  type VendorActionItem,
} from '../lib/vendorActions.ts';

// Statuses that mean the vendor is not (or not yet) fully compliant and should
// be directed to the questionnaire to take action (task 13).
const NON_COMPLIANT_STATUSES = new Set<CompanyStatus>([
  'invited',
  'in_progress',
  'review',
  'changes',
  'expired',
]);

const DAY_MS = 24 * 60 * 60 * 1000;

function daysRemaining(expiresAt: string, now: Date = new Date()): number {
  return Math.ceil((new Date(expiresAt).getTime() - now.getTime()) / DAY_MS);
}

// Left-border + accent text per expiry state for the Document Expiries rows.
// 'none' never occurs here (rows are filtered to files WITH an expiry date).
const EXPIRY_ROW: Record<ExpiryState, { border: string; text: string }> = {
  expired: { border: '#EF4444', text: 'text-red-600' },
  expiring: { border: '#F59E0B', text: 'text-amber-600' },
  ok: { border: '#10B981', text: 'text-emerald-600' },
  none: { border: '#10B981', text: 'text-emerald-600' },
};

export function CompanyHome() {
  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: () => companyApi.get<{ company: Company }>('/api/me'),
  });
  const submissionsQuery = useQuery({
    queryKey: ['me', 'submissions'],
    queryFn: () => companyApi.get<{ submissions: Submission[] }>('/api/me/submissions'),
  });
  const questionsQuery = useQuery({
    queryKey: ['/api/me/questions'],
    queryFn: () => companyApi.get<{ questions: Question[] }>('/api/me/questions'),
  });
  const questionnairesQuery = useQuery({
    queryKey: ['/api/me/questionnaires'],
    queryFn: () => companyApi.get<{ questionnaires: Questionnaire[] }>('/api/me/questionnaires'),
  });

  if (meQuery.isLoading || submissionsQuery.isLoading || questionsQuery.isLoading || questionnairesQuery.isLoading) return <CenteredSpinner />;
  const company = meQuery.data?.company;
  if (!company) return null;

  const submissions = submissionsQuery.data?.submissions ?? [];
  const allQuestions = questionsQuery.data?.questions ?? [];
  const questionnaires = questionnairesQuery.data?.questionnaires ?? [];
  const subByQ = new Map(submissions.map((s) => [s.question_id, s]));

  type ActionRow =
    | { kind: 'action'; id: string; name: string; count: number; summary: string }
    | { kind: 'continue'; id: string; name: string; progress: number };

  const actionRows: ActionRow[] = [];
  let totalActionItems = 0;
  for (const qn of questionnaires) {
    const scoped = allQuestions.filter((q) => q.questionnaire_id === qn.id);
    if (scoped.length === 0) continue;
    const breakdown = computeProgress(scoped, submissions);
    const submitted = isQuestionnaireSubmitted(company.status, scoped, subByQ);
    const visible = scoped.filter((q) => breakdown.visibleIds.has(q.id));
    const items: VendorActionItem[] = questionnaireActionItems(
      visible,
      subByQ,
      breakdown.completionByQuestion,
    );
    if (items.length > 0) {
      totalActionItems += items.length;
      actionRows.push({
        kind: 'action',
        id: qn.id,
        name: qn.name,
        count: items.length,
        summary: actionReasonSummary(items),
      });
    } else if (!submitted && breakdown.progress < 100) {
      actionRows.push({ kind: 'continue', id: qn.id, name: qn.name, progress: breakdown.progress });
    }
  }

  const now = new Date();
  const stats = uploadDocStats(submissions, now);
  // Task 14: attach question_id to each file so rows can deep-link to the
  // Documents page with ?question=<id>.
  const expiryFiles = submissions
    .flatMap((s) =>
      s.submission_files.map((f) => ({ ...f, question_id: s.question_id })),
    )
    .filter(
      (f): f is SubmissionFile & { expires_at: string; question_id: string } =>
        !f.superseded_at && !!f.expires_at,
    )
    .sort((a, b) => a.expires_at.localeCompare(b.expires_at));

  const docsCard =
    stats.expired > 0
      ? { value: `${stats.expired} expired`, cls: 'text-red-600' }
      : stats.expiring > 0
        ? { value: `${stats.expiring} expiring`, cls: 'text-amber-600' }
        : { value: 'All current', cls: 'text-emerald-600' };

  return (
    <div className="space-y-5">
      <h1 className="text-xl font-semibold text-slate-900">My Dashboard</h1>

      {/* Welcome banner */}
      <div
        className="rounded-xl px-5 py-4 text-white"
        style={{ background: 'var(--brand-primary)' }}
      >
        <div className="text-xs opacity-80">Welcome back,</div>
        <div className="text-xl font-bold">{company.contact_name}</div>
        <div className="mt-0.5 text-xs opacity-80">
          {company.company_name}
          {company.station ? ` · ${company.station}` : ''}
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {/* Task 13: non-compliant status badge links to the questionnaire */}
        <StatCard label="Status">
          {NON_COMPLIANT_STATUSES.has(company.status) ? (
            <Link to="/c/questionnaire" className="inline-block">
              <CompanyStatusBadge status={company.status} audience="vendor" />
            </Link>
          ) : (
            <CompanyStatusBadge status={company.status} audience="vendor" />
          )}
        </StatCard>
        <StatCard label="Documents">
          <span className={`text-sm font-bold ${docsCard.cls}`}>{docsCard.value}</span>
        </StatCard>
        <StatCard label="Questionnaire">
          {totalActionItems > 0 ? (
            <Link to="/c/questionnaire" className="text-sm font-bold text-red-600 hover:underline">
              {totalActionItems} need action
            </Link>
          ) : (
            <span className="text-sm font-bold" style={{ color: 'var(--brand-primary)' }}>
              {company.progress}% complete
            </span>
          )}
        </StatCard>
      </div>

      {/* Needs your action — design imported from Claude Design
          (NeedsYourAction.dc.html): warm amber card, orange count chips, a
          per-row category/action summary and an "N items" status pill. */}
      {actionRows.length > 0 && (
        <section
          className="overflow-hidden rounded-xl border border-[#F3D592] bg-[#FFFAF0]"
          style={{
            boxShadow: '0 1px 2px rgba(120,80,10,0.05), 0 6px 16px rgba(120,80,10,0.08)',
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 pb-[18px] pt-[22px]">
            <h2 className="flex items-center gap-3">
              <span className="flex h-7 w-7 flex-none items-center justify-center rounded-full bg-[#FF6600] text-xs font-semibold text-white">
                {actionRows.length}
              </span>
              <span className="text-base font-semibold text-[#09161E]">Needs your action</span>
            </h2>
            <Link
              to="/c/questionnaire"
              className="inline-flex items-center gap-1.5 whitespace-nowrap text-sm font-medium text-[#E0560C] hover:underline"
            >
              Review all <span className="text-[15px]">→</span>
            </Link>
          </div>

          <div className="mx-6 h-px bg-[#F1DEAF]" />

          {/* Items */}
          <div className="px-3 pb-3.5 pt-1.5">
            {actionRows.map((row) => {
              const badge = row.kind === 'action' ? String(row.count) : `${row.progress}%`;
              const subtitle =
                row.kind === 'action' ? row.summary : 'Continue your questionnaire';
              const pill =
                row.kind === 'action'
                  ? `${row.count} item${row.count === 1 ? '' : 's'}`
                  : `${row.progress}% complete`;
              return (
                <Link
                  key={row.id}
                  to={
                    row.kind === 'action'
                      ? `/c/questionnaire?type=${row.id}&action=1`
                      : `/c/questionnaire?type=${row.id}`
                  }
                  className="flex items-center gap-4 rounded-[10px] px-3 py-4 transition hover:bg-[#FCEFCF]"
                >
                  <span className="flex h-11 w-11 flex-none items-center justify-center rounded-[10px] bg-[#FF6600] text-white">
                    <span
                      className={`font-semibold leading-none ${
                        row.kind === 'action' ? 'text-base' : 'text-sm'
                      }`}
                    >
                      {badge}
                    </span>
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="mb-0.5 text-sm font-semibold text-[#09161E]">{row.name}</div>
                    <div className="text-sm text-[#667085]">{subtitle}</div>
                  </div>
                  <div className="flex flex-none items-center gap-3.5">
                    <span className="whitespace-nowrap rounded-full border border-[#F1DEAF] bg-white px-2.5 py-[5px] text-[11px] font-medium uppercase tracking-[0.04em] text-[#8A5A06]">
                      {pill}
                    </span>
                    <span className="text-lg leading-none text-[#98A2B3]">›</span>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* Document expiries */}
      {expiryFiles.length > 0 && (
        <section className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-900">Document Expiries</h2>
            <Link
              to="/c/documents"
              className="text-xs font-medium hover:underline"
              style={{ color: 'var(--brand-primary)' }}
            >
              View all →
            </Link>
          </div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {expiryFiles.map((f) => {
              const state = expiryState(f, now);
              const row = EXPIRY_ROW[state];
              const days = daysRemaining(f.expires_at, now);
              // Task 14: deep-link to the specific question on the Documents page.
              const docsHref = f.question_id
                ? `/c/documents?question=${f.question_id}`
                : '/c/documents';
              return (
                <Link
                  key={f.id}
                  to={docsHref}
                  className="block rounded-md border-l-4 bg-slate-50 px-3 py-2 transition hover:brightness-95"
                  style={{ borderLeftColor: row.border }}
                >
                  <div
                    className="truncate text-xs font-medium"
                    style={{ color: 'var(--brand-primary)' }}
                  >
                    {f.file_name}
                  </div>
                  <div className="text-sm font-semibold text-slate-900">
                    {formatDateOnly(f.expires_at)}
                  </div>
                  <div className={`text-xs ${row.text}`}>
                    {state === 'expired'
                      ? 'Expired'
                      : `${days} day${days === 1 ? '' : 's'} remaining`}
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* Quick info */}
      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <h2 className="mb-2 text-sm font-semibold text-slate-900">Quick Info</h2>
        <dl className="divide-y divide-slate-100">
          <InfoRow
            label="Contractor Type"
            value={
              company.contractor_types && company.contractor_types.length > 0
                ? company.contractor_types.join(', ')
                : null
            }
          />
          <InfoRow label="Vendor Number" value={company.vendor_number} />
          <InfoRow label="Station" value={company.station} />
          <InfoRow label="Cluster Region" value={company.cluster_region} />
        </dl>
      </section>
    </div>
  );
}

function StatCard({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="flex items-center justify-between py-2.5">
      <dt className="text-sm text-slate-500">{label}</dt>
      <dd className="text-sm font-semibold text-slate-900">{value ?? '—'}</dd>
    </div>
  );
}
